import * as React from "react";
import { useMutation } from "urql";
import * as stylex from "@stylexjs/stylex";
import { Button } from "@zendeskgarden/react-buttons";
import { Dots } from "@zendeskgarden/react-loaders";
import { Notification, useToast } from "@zendeskgarden/react-notifications";
import { type IIssueIntakeProcessZendeskTicketMutation, IssueIntakeProcessZendeskTicketMutation } from "~/queries";
import type { ZendeskTicket } from "~/types";
import "../styles/variables.stylex";
import { ZendeskThemeColors, ZendeskThemeVariables } from "../styles/variables.stylex";
type Props = {
  /** Zendesk ticket information. */
  ticket: ZendeskTicket;
  /** Callback to go back to main view. */
  onBack(): void;
};
type ProcessingState = "directiveInput" | "processing";

/**
 * Collects optional instructions and asks Linear Agent to process the current Zendesk ticket. Returns to the main view
 * when processing resolves, with a notification for errors or no created issues.
 *
 * @param props The component properties described by {@link Props} .
 * @returns The rendered content, or `null` when there is nothing to display.
 */
export function AiIntake(props: Props) {
  const {
    ticket,
    onBack
  } = props;
  const [state, setState] = React.useState<ProcessingState>("directiveInput");
  const [directive, setDirective] = React.useState("");
  const {
    addToast
  } = useToast();
  const [, processTicket] = useMutation<IIssueIntakeProcessZendeskTicketMutation>(IssueIntakeProcessZendeskTicketMutation);
  const handleSubmitDirective = () => {
    const latestNonLinearMessageTimestamp = [...ticket.allMessages].reverse().find(m => m.subject !== "Linear")?.timestamp;
    setState("processing");
    void processTicket({
      ticketId: Number(ticket.id),
      ticketUrl: ticket.url,
      latestMessageTimestamp: latestNonLinearMessageTimestamp,
      directive: directive || undefined
    }).then(result => {
      if (result.data?.issueIntakeProcessZendeskTicket.success) {
        const issues = result.data.issueIntakeProcessZendeskTicket.issues;
        if (issues.length === 0) {
          addToast(({
            close
          }) => <Notification type="info" className="sx-1gfrnbc">
                <Notification.Title>No issues were created</Notification.Title>
                <Notification.Close aria-label="Close" onClick={close} />
              </Notification>, {
            placement: "bottom-end"
          });
        }
      } else {
        addToast(({
          close
        }) => <Notification type="error" className="sx-1gfrnbc">
              <Notification.Title>Error processing ticket</Notification.Title>
              <div>Something went wrong while processing the ticket. Please try again.</div>
              <Notification.Close aria-label="Close" onClick={close} />
            </Notification>, {
          placement: "bottom-end"
        });
      }
      onBack();
    });
  };
  if (state === "directiveInput") {
    return <div className="sx-10dr8xq sx-njsko4">
        <div className="sx-1j61zf2 sx-1s688f sx-1e56ztr">Create with Linear Agent</div>
        <div className="sx-8qomae sx-if65rj sx-1yztbdb">Provide any specific instructions for creating issues from the ticket</div>
        <textarea placeholder="Additional instructions (optional)" value={directive} onChange={e => setDirective(e.target.value)} className="sx-h8yej3 sx-e8ttls sx-mkeg23 sx-1y0btm7 sx-1ke0ykx sx-16s4ml sx-12oqio5 sx-jb2p0i sx-if65rj sx-1evy7pa sx-288g5 sx-seoqlg sx-1yztbdb sx-1uvtmcs sx-dqyio7" />
        <div className="sx-78zum5 sx-dt5ytf sx-167g77z">
          <Button isStretched isPrimary onClick={handleSubmitDirective} className="sx-dj266r">
            Create
          </Button>
          <Button isStretched onClick={onBack} className="sx-dj266r">
            Cancel
          </Button>
        </div>
      </div>;
  }
  if (state === "processing") {
    return <div className="sx-10dr8xq sx-njsko4">
        <div className="sx-78zum5 sx-6s0dn4 sx-167g77z">
          <div className="sx-1j61zf2 sx-1s688f sx-1e56ztr">Creating issues with Linear Agent</div>
          <Dots className="sx-zueoph" />
        </div>
        <div className="sx-8qomae sx-if65rj sx-1yztbdb">Creating issues from the ticket. This may take up to 30 seconds</div>
      </div>;
  }
  return null;
}